import { API, formatCPF, formatCEP, formatMoney, formatPhone } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { CheckCircle2, FileText } from "lucide-react";

export const STATUSES = ["Recebida","Em Triagem","Documentação Pendente","Em Análise","Aprovada","Indeferida","Homologada"];

const STATUS_COLORS = {
  "Recebida": "bg-blue-100 text-blue-800",
  "Em Triagem": "bg-yellow-100 text-yellow-800",
  "Documentação Pendente": "bg-orange-100 text-orange-800",
  "Em Análise": "bg-purple-100 text-purple-800",
  "Aprovada": "bg-green-100 text-green-800",
  "Indeferida": "bg-red-100 text-red-800",
  "Homologada": "bg-emerald-100 text-emerald-800",
};
export const statusColor = (s) => STATUS_COLORS[s] || "bg-slate-100";

const isCasado = (ec) => ec === "Casado" || ec === "União Estável";

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-1.5 border-b border-slate-100 text-sm">
      <div className="text-slate-500 font-medium">{label}</div>
      <div className="col-span-2">{value || "-"}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

export function DadosPessoaisCard({ insc }) {
  return (
    <Section title="Dados Pessoais">
      <Row label="CPF" value={formatCPF(insc.cpf)} />
      <Row label="RG" value={insc.rg} />
      <Row label="Data Nascimento" value={insc.data_nascimento} />
      <Row label="Sexo" value={insc.sexo} />
      <Row label="Estado Civil" value={insc.estado_civil} />
      <Row label="Telefone" value={formatPhone(insc.telefone)} />
      <Row label="E-mail" value={insc.email} />
    </Section>
  );
}

export function ConjugeCard({ insc }) {
  if (!isCasado(insc.estado_civil)) return null;
  return (
    <Section title="Cônjuge">
      <Row label="Nome" value={insc.conjuge_nome} />
      <Row label="CPF" value={insc.conjuge_cpf} />
      <Row label="RG" value={insc.conjuge_rg} />
      <Row label="Nascimento" value={insc.conjuge_data_nascimento} />
    </Section>
  );
}

export function EnderecoCard({ insc }) {
  return (
    <Section title="Endereço">
      <Row label="CEP" value={formatCEP(insc.cep)} />
      <Row label="Endereço" value={`${insc.rua}, ${insc.numero} ${insc.complemento || ""}`} />
      <Row label="Bairro" value={insc.bairro} />
      <Row label="Cidade/UF" value={`${insc.cidade}/${insc.estado}`} />
      {insc.tipo_moradia && <Row label="Tipo de moradia" value={insc.tipo_moradia} />}
      {insc.tipo_moradia === "Própria" && <Row label="Em área de risco" value={insc.area_de_risco ? "Sim" : "Não"} />}
    </Section>
  );
}

export function FamiliaSocialCard({ insc }) {
  return (
    <Section title="Família & Social">
      <Row label="Moradores" value={insc.qtd_moradores} />
      <Row label="Dependentes" value={insc.qtd_dependentes} />
      <Row label="Crianças" value={insc.qtd_criancas} />
      <Row label="Idosos" value={insc.qtd_idosos} />
      <Row label="PCD" value={insc.qtd_pcd} />
      <Row label="Escolaridade" value={insc.grau_escolaridade} />
      <Row label="Ocupação" value={insc.ocupacao} />
      <Row label="CadÚnico" value={insc.possui_cadunico ? "Sim" : "Não"} />
      <Row label="NIS" value={insc.nis} />
      <Row label="Bolsa Família" value={insc.recebe_bolsa_familia ? "Sim" : "Não"} />
      <Row label="PNE na família" value={insc.possui_pcd ? `Sim (${insc.qtd_pcd || 0})` : "Não"} />
      {insc.possui_pcd && insc.tipo_deficiencia && <Row label="Tipo de deficiência" value={insc.tipo_deficiencia} />}
      {insc.possui_pcd && insc.cid_pne && <Row label="CID" value={insc.cid_pne} />}
      {insc.possui_pcd && insc.descricao_pcd && <Row label="Descrição PNE" value={insc.descricao_pcd} />}
      {insc.possui_pcd && <Row label="PNE é o responsável" value={insc.pne_e_responsavel ? "Sim" : "Não"} />}
      <Row label="Idoso na residência" value={insc.possui_idoso ? `Sim (${insc.qtd_idosos || 0})` : "Não"} />
      {insc.possui_idoso && insc.idade_idoso && <Row label="Idade do idoso" value={insc.idade_idoso} />}
      {insc.possui_idoso && insc.descricao_idoso && <Row label="Obs. idoso" value={insc.descricao_idoso} />}
      {insc.possui_idoso && <Row label="Idoso é o responsável" value={insc.idoso_e_responsavel ? "Sim" : "Não"} />}
    </Section>
  );
}

export function RendaCard({ insc }) {
  return (
    <Section title="Renda">
      <Row label="Responsável" value={formatMoney(insc.renda_responsavel)} />
      <Row label="Cônjuge" value={formatMoney(insc.renda_conjuge)} />
      <Row label="Outras" value={formatMoney(insc.outras_rendas)} />
      <Row label="Total" value={<strong>{formatMoney(insc.renda_total)}</strong>} />
    </Section>
  );
}

export function DocumentosCard({ insc, id }) {
  const docs = insc.documentos || [];
  return (
    <Section title="Documentos Anexados">
      {docs.length === 0 ? (
        <p className="text-sm text-slate-500">Nenhum documento anexado.</p>
      ) : (
        <ul className="space-y-2">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center gap-2 text-sm border rounded p-2">
              <FileText className="w-4 h-4 text-slate-400" />
              <span className="flex-1 truncate">{d.filename} <span className="text-xs text-slate-400">· {d.tipo}</span></span>
              <Button size="sm" variant="outline" data-testid={`btn-abrir-doc-${d.id}`} onClick={() => window.open(`${API}/inscricoes/${id}/documentos/${d.id}/download`, "_blank")}>Abrir</Button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

export function AnalisePanel({ novoStatus, setNovoStatus, parecer, setParecer, onSave }) {
  return (
    <Section title="Análise / Status">
      <div className="space-y-3">
        <div>
          <Label>Novo Status</Label>
          <Select value={novoStatus} onValueChange={setNovoStatus}>
            <SelectTrigger data-testid="select-status"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Parecer Técnico</Label>
          <Textarea value={parecer} onChange={(e) => setParecer(e.target.value)} rows={4} data-testid="textarea-parecer" />
        </div>
        <Button onClick={onSave} className="w-full bg-[#00883A] hover:bg-[#007030]" data-testid="btn-salvar-status">
          <CheckCircle2 className="w-4 h-4 mr-2" />Salvar
        </Button>
      </div>
    </Section>
  );
}

export function HistoricoCard({ historico }) {
  const items = (historico || []).slice().reverse();
  return (
    <Section title="Histórico">
      <ul className="space-y-3 text-xs">
        {items.map((h) => (
          <li key={`${h.data}-${h.acao}`} className="border-l-2 border-[#083CA6] pl-3">
            <div className="font-semibold">{h.acao}</div>
            <div className="text-slate-500">{new Date(h.data).toLocaleString("pt-BR")}</div>
            {h.detalhe && <div className="text-slate-600 mt-1">{h.detalhe}</div>}
          </li>
        ))}
      </ul>
    </Section>
  );
}

export function StatusHeaderBadges({ insc }) {
  return (
    <div className="flex items-center gap-2 mt-1">
      <span className="text-xs font-mono bg-slate-100 px-2 py-0.5 rounded">{insc.protocolo}</span>
      <Badge className={statusColor(insc.status)}>{insc.status}</Badge>
      {insc.apta_faixa1 ? (
        <Badge className="bg-green-100 text-green-800">Apta Faixa 1</Badge>
      ) : (
        <Badge variant="outline" className="text-red-600">Fora Faixa 1</Badge>
      )}
    </div>
  );
}
